import { useCallback } from 'react';
import { useMapStore } from '@/stores/mapStore';
import { useUIStore } from '@/stores/uiStore';
import { useTrafficStore } from './trafficStore';
import { useIsochroneStore } from './isochroneStore';
import { AccountButton } from '@/components/auth/AccountButton';
import { formatAccuracy } from '@/hooks/useGeolocation';
import { deviceHeading } from '@/services/deviceHeading';
import config from '@/config';
import type { MapStyle } from '@/types';

// Only expose base styles that have a real data source. 'terrain' and 'transit'
// were stubs that loaded an unrelated fallback style, so they are omitted;
// 'satellite' only appears when an aerial style URL is actually configured.
const mapStyles: { id: MapStyle; name: string; icon: string }[] = [
  { id: 'default', name: 'Default', icon: 'M' },
  { id: 'dark', name: 'Dark', icon: 'D' },
  ...(config.map.styles.satellite
    ? [{ id: 'satellite' as MapStyle, name: 'Satellite', icon: 'S' }]
    : []),
];

export function MapControls() {
  const {
    currentStyle,
    setCurrentStyle,
    show3DBuildings,
    showTerrain,
    toggleLayer,
    viewport,
    setStreetViewCoordinates,
    userLocationState,
    isTrackingLocation,
    isFollowingUser,
    setIsFollowingUser,
    goToUserLocation,
    showLocationPanel,
    setShowLocationPanel,
    setUserLocationState,
    setLocationPermission,
    setLocationError,
  } = useMapStore();

  const { panels, togglePanel, addNotification } = useUIStore();

  // Any non-search panel open means a bottom sheet covers the lower-right on
  // mobile — hide the floating map controls there so they aren't buried under it.
  const anyPanelOpen = Object.entries(panels).some(([k, v]) => v && k !== 'searchPanel');

  const trafficEnabled = useTrafficStore((s) => s.enabled);
  const toggleTraffic = useTrafficStore((s) => s.toggle);

  const isochroneActive = useIsochroneStore((s) => s.active);
  const toggleIsochrone = useIsochroneStore((s) => s.toggleActive);

  const handleStyleChange = useCallback((style: MapStyle) => {
    setCurrentStyle(style);
  }, [setCurrentStyle]);

  const handleStreetViewClick = useCallback(() => {
    // Open street view at current map center
    setStreetViewCoordinates(viewport.center);
    addNotification({
      type: 'info',
      message: 'Entering Street View at current location',
      duration: 2000,
    });
  }, [viewport.center, setStreetViewCoordinates, addNotification]);

  const handleMyLocationClick = useCallback(() => {
    // Enable the device compass from this tap (iOS needs a user gesture) so the
    // marker can show which way you're facing.
    void deviceHeading.enable();
    // Engage "follow me": MapContainer starts continuous tracking (if needed) and
    // recentres the camera on every fix, so the map moves with you as you travel.
    setIsFollowingUser(true);
    if (userLocationState) {
      goToUserLocation();
      addNotification({
        type: 'info',
        message: `Following your location (${formatAccuracy(userLocationState.accuracy)} accuracy)`,
        duration: 2000,
      });
    } else {
      // Request location permission directly via browser API
      if (!navigator.geolocation) {
        addNotification({
          type: 'error',
          message: 'Geolocation is not supported by this browser.',
          duration: 3000,
        });
        return;
      }

      addNotification({
        type: 'info',
        message: 'Requesting location access...',
        duration: 2000,
      });

      navigator.geolocation.getCurrentPosition(
        (position) => {
          setLocationPermission('granted');
          setUserLocationState({
            coordinates: {
              lat: position.coords.latitude,
              lng: position.coords.longitude,
            },
            accuracy: position.coords.accuracy,
            altitude: position.coords.altitude ?? undefined,
            altitudeAccuracy: position.coords.altitudeAccuracy ?? undefined,
            heading: position.coords.heading ?? undefined,
            speed: position.coords.speed ?? undefined,
            timestamp: position.timestamp,
            source: position.coords.accuracy < 100 ? 'gps' : 'network',
          });
          setLocationError(null);
          goToUserLocation();
          addNotification({
            type: 'success',
            message: `Location found (${formatAccuracy(position.coords.accuracy)} accuracy)`,
            duration: 2000,
          });
        },
        (error) => {
          if (error.code === error.PERMISSION_DENIED) {
            setLocationPermission('denied');
            addNotification({
              type: 'error',
              message: 'Location permission denied. Please allow access in your browser.',
              duration: 4000,
            });
          } else if (error.code === error.POSITION_UNAVAILABLE) {
            addNotification({
              type: 'error',
              message: 'Location unavailable. Please enable GPS/location services.',
              duration: 4000,
            });
          } else {
            addNotification({
              type: 'error',
              message: 'Could not get location. Please try again.',
              duration: 3000,
            });
          }
        },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
      );
    }
  }, [userLocationState, goToUserLocation, addNotification, setLocationPermission, setUserLocationState, setLocationError, setIsFollowingUser]);

  const handleLocationPanelToggle = useCallback(() => {
    setShowLocationPanel(!showLocationPanel);
  }, [showLocationPanel, setShowLocationPanel]);

  return (
    <>
      {/* Action rail - top right (icon-only with tooltips) */}
      <div className="absolute top-4 right-4 z-controls">
        <div className="flex flex-col gap-1 rounded-2xl bg-white/95 p-1.5 shadow-card-hover backdrop-blur max-md:max-h-[calc(100dvh-2rem)] max-md:overflow-y-auto max-md:scrollbar-none">
          <AccountButton />
          <div className="mx-1 my-0.5 h-px bg-gray-200" />
          <RailButton label="Layers" active={panels.layersPanel} onClick={() => togglePanel('layersPanel')}>
            <LayersIcon className="h-5 w-5" />
          </RailButton>
          <RailButton label="Nearby" active={panels.nearbyPanel} onClick={() => togglePanel('nearbyPanel')}>
            <ExploreIcon className="h-5 w-5" />
          </RailButton>
          <RailButton label="Saved" active={panels.savedPlacesPanel} onClick={() => togglePanel('savedPlacesPanel')}>
            <StarIcon className="h-5 w-5" />
          </RailButton>
          <RailButton label="Measure" active={panels.measurePanel} onClick={() => togglePanel('measurePanel')}>
            <RulerIcon className="h-5 w-5" />
          </RailButton>
          {/* Reachable area (isochrone) explorer — enters an on-map mode; state
              lives in the local isochroneStore (self-contained like Traffic). */}
          <RailButton label="Reachable area" active={isochroneActive} onClick={toggleIsochrone}>
            <IsochroneIcon className="h-5 w-5" />
          </RailButton>
          {config.features.streetView && (
            <RailButton label="Street View" onClick={handleStreetViewClick}>
              <StreetViewIcon className="h-5 w-5" />
            </RailButton>
          )}
          <RailButton label="Shortcuts" active={panels.shortcutsPanel} onClick={() => togglePanel('shortcutsPanel')}>
            <KeyboardIcon className="h-5 w-5" />
          </RailButton>
        </div>

        {/* Layers panel - opens to the LEFT of the vertical rail so it doesn't overlap it */}
        {panels.layersPanel && (
          <div className="absolute top-0 right-full mr-2 w-64 max-w-[calc(100vw-5rem)] bg-white rounded-lg shadow-dropdown animate-fade-in">
            {/* Map type section */}
            <div className="p-3 border-b border-gray-200">
              <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
                Map type
              </h3>
              <div
                className="grid gap-1"
                style={{ gridTemplateColumns: `repeat(${mapStyles.length}, minmax(0, 1fr))` }}
              >
                {mapStyles.map((style) => (
                  <button
                    key={style.id}
                    onClick={() => handleStyleChange(style.id)}
                    className={`
                      flex flex-col items-center p-2 rounded-lg transition-colors
                      ${currentStyle === style.id
                        ? 'bg-primary-100 text-primary-700'
                        : 'hover:bg-gray-100 text-gray-700'
                      }
                    `}
                    title={style.name}
                  >
                    <span className="text-lg font-medium">{style.icon}</span>
                    <span className="text-[10px] mt-1">{style.name}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Map details section */}
            <div className="p-3">
              <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
                Map details
              </h3>
              <div className="space-y-2">
                {/* Live traffic overlay. Backed by the live traffic-service; gated
                    behind config.features.traffic so it only appears where the
                    feature is enabled. Off by default. */}
                {config.features.traffic && (
                  <LayerToggle
                    label="Traffic"
                    checked={trafficEnabled}
                    onChange={toggleTraffic}
                  />
                )}
                <LayerToggle
                  label="3D Buildings"
                  checked={show3DBuildings}
                  onChange={() => toggleLayer('buildings')}
                />
                {/* Terrain needs a raster-DEM source that isn't part of the default
                    tile stack; only shown when explicitly enabled via config. */}
                {config.features.terrain && (
                  <LayerToggle
                    label="Terrain"
                    checked={showTerrain}
                    onChange={() => toggleLayer('terrain')}
                  />
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* My Location button — top of the bottom-right FAB rail (above GeoChat
          @bottom-40 and ReportHazard @bottom-24; 64px pitch keeps the 48px FABs
          from overlapping). */}
      <div className={`absolute bottom-56 right-4 z-controls ${anyPanelOpen ? 'max-md:hidden' : ''}`}>
        <div className="flex flex-col gap-2">
          {/* My Location Button */}
          <button
            onClick={handleMyLocationClick}
            onContextMenu={(e) => {
              e.preventDefault();
              handleLocationPanelToggle();
            }}
            className={`
              w-10 h-10 max-md:w-11 max-md:h-11 bg-white rounded-lg shadow-card flex items-center justify-center
              transition-all hover:bg-gray-50 active:scale-95
              ${isFollowingUser ? 'ring-2 ring-primary-600 bg-primary-50' : isTrackingLocation ? 'ring-2 ring-primary-400' : ''}
              ${showLocationPanel && !isFollowingUser ? 'bg-primary-50' : ''}
            `}
            title={isFollowingUser
              ? 'Following your location — pan the map to stop. Right-click for details.'
              : userLocationState
                ? `Center & follow your location (${formatAccuracy(userLocationState.accuracy)}). Right-click for details.`
                : 'Get my location. Right-click for details.'}
          >
            <MyLocationIcon
              className={`w-5 h-5 ${
                userLocationState
                  ? isTrackingLocation
                    ? 'text-primary-600'
                    : 'text-primary-500'
                  : 'text-gray-400'
              }`}
              filled={!!userLocationState}
            />
            {isTrackingLocation && (
              <span className="absolute top-1 right-1 w-2 h-2 bg-green-500 rounded-full animate-pulse" />
            )}
          </button>
        </div>
      </div>

      {/* Zoom indicator — bottom LEFT, out of the right-side FAB rail so it never
          collides with the My Location / hazard / GeoChat stack. Purely
          informational, so hidden on small screens. */}
      <div className="absolute bottom-6 left-4 z-controls hidden sm:block">
        <ZoomDisplay />
      </div>

    </>
  );
}

function RailButton({
  label,
  active,
  onClick,
  children,
}: {
  label: string;
  active?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      title={label}
      aria-label={label}
      aria-pressed={active}
      className={`group relative flex h-10 w-10 max-md:h-11 max-md:w-11 items-center justify-center rounded-xl transition-colors ${
        active ? 'bg-primary-50 text-primary-600' : 'text-gray-600 hover:bg-gray-100'
      }`}
    >
      {children}
      <span className="pointer-events-none absolute right-full mr-2 whitespace-nowrap rounded-md bg-gray-900 px-2 py-1 text-xs text-white opacity-0 shadow-lg transition-opacity group-hover:opacity-100">
        {label}
      </span>
    </button>
  );
}

function LayerToggle({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: () => void;
}) {
  return (
    <label className="flex items-center justify-between cursor-pointer group">
      <span className="text-sm text-gray-700 group-hover:text-gray-900">
        {label}
      </span>
      <div className="relative">
        <input
          type="checkbox"
          checked={checked}
          onChange={onChange}
          className="sr-only"
        />
        <div
          className={`
            w-10 h-6 rounded-full transition-colors
            ${checked ? 'bg-primary-600' : 'bg-gray-300'}
          `}
        >
          <div
            className={`
              absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-transform
              ${checked ? 'translate-x-5' : 'translate-x-1'}
            `}
          />
        </div>
      </div>
    </label>
  );
}

function ZoomDisplay() {
  const { viewport } = useMapStore();
  return (
    <div className="bg-white px-2 py-1 rounded shadow-card text-xs text-gray-600">
      Zoom: {viewport.zoom.toFixed(1)}
    </div>
  );
}

function LayersIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
      />
    </svg>
  );
}

function ExploreIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
      />
    </svg>
  );
}

function StarIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
      />
    </svg>
  );
}

function RulerIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M6 6l12 12M6 18V6h12"
      />
    </svg>
  );
}

function StreetViewIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <circle cx="12" cy="8" r="4" strokeWidth={2} />
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M12 12v8M8 16h8"
      />
    </svg>
  );
}

function IsochroneIcon({ className }: { className?: string }) {
  // Concentric rings around a center dot — evokes nested reachability contours.
  return (
    <svg
      className={className}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <circle cx="12" cy="12" r="9" strokeWidth={1.5} />
      <circle cx="12" cy="12" r="5.5" strokeWidth={1.5} />
      <circle cx="12" cy="12" r="2" strokeWidth={2} fill="currentColor" />
    </svg>
  );
}

function KeyboardIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <rect x="2" y="6" width="20" height="12" rx="2" strokeWidth={2} />
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M6 10h.01M10 10h.01M14 10h.01M18 10h.01M6 14h12"
      />
    </svg>
  );
}

function MyLocationIcon({ className, filled }: { className?: string; filled?: boolean }) {
  return (
    <svg
      className={className}
      fill={filled ? 'currentColor' : 'none'}
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <circle cx="12" cy="12" r="4" strokeWidth={2} fill={filled ? 'currentColor' : 'none'} />
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M12 2v4m0 12v4M2 12h4m12 0h4"
      />
      <circle cx="12" cy="12" r="8" strokeWidth={2} fill="none" />
    </svg>
  );
}
